import React, { useRef, useEffect } from "react";
import { Box } from "@mui/material";

const LENGTH = 6;

// Seis casillas para el codigo TOTP de Google Authenticator.
// Avanza sola al escribir, retrocede con backspace, acepta pegar el codigo
// completo y solo deja digitos.
const OtpInput = ({ value, onChange, onComplete, disabled, autoFocus }) => {
  const refs = useRef([]);

  useEffect(() => {
    if (autoFocus && refs.current[0]) refs.current[0].focus();
  }, [autoFocus]);

  const digits = value.padEnd(LENGTH, " ").slice(0, LENGTH).split("");

  const setAt = (index, digit) => {
    const next = digits.map((d, i) => (i === index ? digit : d)).join("");
    const cleaned = next.replace(/\s+$/, "");
    onChange(cleaned);
    return cleaned;
  };

  const handleChange = (index) => (event) => {
    const typed = event.target.value.replace(/\D/g, "");
    if (!typed) return;

    // Si pegaron el codigo completo se reparte entre todas las casillas.
    if (typed.length > 1) {
      const filled = typed.slice(0, LENGTH);
      onChange(filled);
      const last = Math.min(filled.length, LENGTH - 1);
      refs.current[last]?.focus();
      if (filled.length === LENGTH) onComplete?.(filled);
      return;
    }

    const updated = setAt(index, typed);
    if (index < LENGTH - 1) refs.current[index + 1]?.focus();
    if (updated.replace(/\s/g, "").length === LENGTH) onComplete?.(updated);
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      if (digits[index] !== " ") {
        setAt(index, " ");
      } else if (index > 0) {
        setAt(index - 1, " ");
        refs.current[index - 1]?.focus();
      }
    }
    if (event.key === "ArrowLeft" && index > 0) refs.current[index - 1]?.focus();
    if (event.key === "ArrowRight" && index < LENGTH - 1)
      refs.current[index + 1]?.focus();
  };

  return (
    <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
      {digits.map((digit, index) => (
        <Box
          key={index}
          component="input"
          ref={(element) => (refs.current[index] = element)}
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={LENGTH}
          disabled={disabled}
          value={digit.trim()}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onFocus={(event) => event.target.select()}
          sx={{
            width: "100%",
            minWidth: 0,
            aspectRatio: "3 / 4",
            maxHeight: 62,
            textAlign: "center",
            bgcolor: "#111e2b",
            border: "1px solid #23394d",
            borderRadius: "9px",
            color: "#e8eef4",
            fontSize: 24,
            fontWeight: 600,
            fontFamily: "'IBM Plex Mono', monospace",
            outline: "none",
            transition: "border-color .15s",
            "&:focus": { borderColor: "#4da3d8" },
            "&:disabled": { opacity: 0.5 },
          }}
        />
      ))}
    </Box>
  );
};

export default OtpInput;
